import { z } from "zod";

import type { RadioGroupOption } from "@luce/ui";

import { IssueTypeEnum, PackageTerminationReasonEnum } from "../../__generated__/graphql";
import type {
  PackageRequestFormValues,
  PackageRequestMode,
  PackageRequestOutcome,
  PackageRequestType,
} from "./types";

export const REQUEST_TYPES: Record<
  PackageRequestType,
  { title: string; description: string; issueType: IssueTypeEnum }
> = {
  "edit-address": {
    title: "Edit Address",
    description:
      "Please provide us a reason to modify your package address, and fill the comment to describe the reason.",
    issueType: IssueTypeEnum.PackageUpdateAddress,
  },
  "modify-plan": {
    title: "Modify Plan",
    description:
      "Please provide us a reason to modify your plan, and fill the comment to describe the reason.",
    issueType: IssueTypeEnum.PackageUpdate,
  },
  cancel: {
    title: "Cancel Plan",
    description: "One more step to cancel your plan, could you let us know the reason.",
    issueType: IssueTypeEnum.PackageTermination,
  },
};

export function getRequestMode(
  type: PackageRequestType,
  hasStarted: boolean | null,
): PackageRequestMode | null {
  if (type !== "cancel") return "request";
  if (hasStarted === null) return null;
  return hasStarted ? "terminate" : "request";
}

const REASON_LABELS: Partial<Record<PackageTerminationReasonEnum, string>> = {
  [PackageTerminationReasonEnum.FrequentCancellation]: "Frequent Cancellation",
  [PackageTerminationReasonEnum.GoingOverseas]: "Going overseas",
  [PackageTerminationReasonEnum.HiredFullTimeCleaner]: "Hired a full-time maid",
  [PackageTerminationReasonEnum.SwitchedToCompetitor]: "Switched to another company",
  [PackageTerminationReasonEnum.TemporaryCleaningOnly]: "Temporary Cleaning Only",
  [PackageTerminationReasonEnum.PoorCleaningQuality]: "Unhappy with my cleaner",
  [PackageTerminationReasonEnum.SchedulingIssue]: "Unhappy with my time slot",
  [PackageTerminationReasonEnum.Others]: "Other reason",
};

export const CANCEL_REASON_OPTIONS: RadioGroupOption[] = Object.entries(REASON_LABELS).map(
  ([value, label]) => ({ value, label }),
);

export function toTerminationReason(value: string): PackageTerminationReasonEnum | null {
  return Object.values(PackageTerminationReasonEnum).find((reason) => reason === value) ?? null;
}

const OTHER_REASON_LABEL = "Other reason";

export function toIssueReasonOptions(issues: { id: string; name: string }[]): RadioGroupOption[] {
  return issues
    .map((issue) => ({ value: issue.id, label: issue.name }))
    .sort((a, b) => {
      if (a.label === OTHER_REASON_LABEL) return 1;
      if (b.label === OTHER_REASON_LABEL) return -1;
      return a.label.localeCompare(b.label);
    });
}

const FOLLOW_UP_REASONS: PackageTerminationReasonEnum[] = [
  PackageTerminationReasonEnum.GoingOverseas,
  PackageTerminationReasonEnum.PoorCleaningQuality,
  PackageTerminationReasonEnum.SchedulingIssue,
  PackageTerminationReasonEnum.Others,
];

export function getTerminatedOutcome(reason: PackageTerminationReasonEnum): PackageRequestOutcome {
  return FOLLOW_UP_REASONS.includes(reason) ? "terminated-follow-up" : "terminated";
}

export const packageRequestSchema = z.object({
  reason: z.string().min(1, { error: "Please select a reason" }),
  comment: z
    .string()
    .min(1, { error: "Comment is required" })
    .min(5, { error: "Comment must be at least 5 characters" }),
});

export const emptyPackageRequestValues: PackageRequestFormValues = { reason: "", comment: "" };

/** The feedback prompt is optional, so it is appended rather than sent as its own field. */
export function buildTerminationComment(comment: string, feedback: string): string {
  const trimmed = feedback.trim();
  return trimmed ? `${comment}\n\nFeedback: ${trimmed}` : comment;
}
